// Board columns for the Prospect and Pipeline Board dashboards. A column is a name plus a set of
// filters ({ field, op, value }) that the server checks:
//   Prospect        contact filters → api/GSDesignContacts/Query (GSDesign's whitelisted fields)
//   Pipeline Board  loan filters    → api/PipelineBoard/Query (whitelisted ReportingView columns,
//                                     every value a SQL parameter; GSWebAPIv2 PipelineBoardService)
// Columns are kept per browser, per board. Nothing here talks to the API.

const KEY = board => `connect.board.${board}`

export function newColumnId() {
  return 'c' + Math.random().toString(36).slice(2, 10)
}

export function cleanColumn(c) {
  return {
    id: c.id || newColumnId(),
    title: String(c.title || '').trim() || 'Untitled column',
    filters: (c.filters || [])
      .filter(f => f && f.field && f.op)
      .map(f => ({ field: f.field, op: f.op, value: f.value === undefined || f.value === null ? '' : String(f.value) }))
  }
}

export function loadBoardColumns(board) {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY(board)) || 'null')
    return Array.isArray(v) && v.length ? v.map(cleanColumn) : null
  } catch (e) { return null }
}

export function saveBoardColumns(board, columns) {
  try { window.localStorage.setItem(KEY(board), JSON.stringify(columns.map(cleanColumn))) } catch (e) {}
}

// A column that is exactly "status is X" (Prospect): cards can be dragged into it to set that status.
export function singleStatusOf(column) {
  const f = column && column.filters
  return f && f.length === 1 && f[0].field === 'status' && f[0].op === 'is' && f[0].value ? f[0].value : ''
}

export function statusColumn(status) {
  return { id: newColumnId(), title: status, filters: [{ field: 'status', op: 'is', value: status }] }
}

// Conditions per field kind. Contacts use GSDesign's set (text / choice / date / yes_no); loans use the
// PipelineBoardService set (choice / text / date / number). Labels are what the editor shows.
export const CONTACT_OPS = {
  text: [['contains', 'contains'], ['is', 'is'], ['is_not', 'is not'], ['starts_with', 'starts with']],
  choice: [['is', 'is'], ['is_not', 'is not']],
  date: [['on_or_after', 'on or after'], ['on_or_before', 'on or before'], ['on', 'on'], ['before', 'before'], ['after', 'after']],
  yes_no: [['yes', 'yes'], ['no', 'no']]
}

export const LOAN_OPS = {
  choice: [['is', 'is'], ['is_not', 'is not'], ['contains', 'contains'], ['is_empty', 'is empty'], ['is_not_empty', 'is not empty']],
  text: [['contains', 'contains'], ['is', 'is'], ['is_not', 'is not'], ['starts_with', 'starts with'], ['is_empty', 'is empty'], ['is_not_empty', 'is not empty']],
  date: [['in_next_days', 'in the next … days'], ['in_last_days', 'in the last … days'], ['on_or_after', 'on or after'], ['on_or_before', 'on or before'],
    ['on', 'on'], ['before', 'before'], ['after', 'after'], ['is_empty', 'is empty'], ['is_not_empty', 'is not empty']],
  number: [['gte', 'at least'], ['lte', 'at most'], ['gt', 'more than'], ['lt', 'less than'], ['eq', 'equals'], ['is_empty', 'is empty'], ['is_not_empty', 'is not empty']]
}

// Conditions that take no value.
export const NO_VALUE_OPS = ['is_empty', 'is_not_empty', 'yes', 'no']

// What a value input should be for a condition: 'none' | 'days' | 'date' | 'number' | 'choice' | 'text'.
export function valueInputFor(kind, op, hasChoices) {
  if (NO_VALUE_OPS.includes(op)) return 'none'
  if (op === 'in_next_days' || op === 'in_last_days') return 'days'
  if (kind === 'date') return 'date'
  if (kind === 'number') return 'number'
  if ((kind === 'choice') && hasChoices && (op === 'is' || op === 'is_not')) return 'choice'
  return 'text'
}

// "Milestone is Submitted · Est. closing in the next 14 days": the column's filters in words.
export function describeFilters(filters, fields, opsByKind) {
  return (filters || []).map(f => {
    const def = (fields || []).find(x => x.key === f.field) || { label: f.field, kind: 'text' }
    const ops = opsByKind[def.kind] || opsByKind.text || []
    const label = (ops.find(o => o[0] === f.op) || [f.op, f.op])[1]
    if (NO_VALUE_OPS.includes(f.op)) return `${def.label} ${label}`
    if (f.op === 'in_next_days') return `${def.label} in the next ${f.value} days`
    if (f.op === 'in_last_days') return `${def.label} in the last ${f.value} days`
    return `${def.label} ${label} ${f.value}`
  }).join(' · ')
}
